/* eslint-disable no-loop-func */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, waitForAuthReady } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';

// Default dayparts
const DEFAULT_DAYPARTS = [
  { id: 'breakfast', name: 'Breakfast', start: '06:00', end: '10:00' },
  { id: 'lunch', name: 'Lunch', start: '11:00', end: '15:00' },
  { id: 'afternoon', name: 'Afternoon', start: '15:00', end: '18:00' },
  { id: 'dinner', name: 'Dinner', start: '18:00', end: '22:00' },
  { id: 'latenight', name: 'Late Night', start: '22:00', end: '02:00' },
];
const MAX_DAYPART_THRESHOLD = 50;

function toMinutes(t) {
  if (!t) return 0;
  const [hh, mm] = t.split(':').map(Number);
  return hh * 60 + mm;
}

function sortDaypartsByStart(dayparts = []) {
  return [...dayparts].sort((a, b) => toMinutes(a?.start) - toMinutes(b?.start));
}



export const WEEKDAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

export default function StaffingTargetsPanel({ open, onClose, roles = [], locationId, locationName = '' }) {
  const [daypartsOpen, setDaypartsOpen] = useState(true);
  const storageKey = `staffingTargets:${locationId || 'global'}`;
  const [dayparts, setDayparts] = useState(DEFAULT_DAYPARTS);
  const [useSameEveryDay, setUseSameEveryDay] = useState(true);
  // use WEEKDAYS constant (module-scope) to keep hook dependencies stable
  // targets: either global (applies to all days) or per-day map
  const [targets, setTargets] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Load from localStorage when opening or location changes
  useEffect(() => {
    if (!open) return;
  (async () => {
      // Wait for auth to be ready so tenant resolution can work reliably
      try {
        await waitForAuthReady();
      } catch (e) {
        // ignore — waitForAuthReady may still resolve to null
      }

      // Try Firestore first (tenant-scoped)
      try {
        const tenantId = await tryResolveTenantId();
        if (tenantId && locationId) {
          const ref = doc(db, `tenants/${tenantId}/staffingTargets`, locationId);
          const snap = await getDoc(ref);
          if (snap && snap.exists()) {
            const data = snap.data();
            setDayparts(sortDaypartsByStart(data.dayparts || DEFAULT_DAYPARTS));
            setUseSameEveryDay(data.useSameEveryDay !== false);
            setTargets(data.targets || {});
            console.info('[staffing] loaded targets from Firestore', { tenantId, locationId });
            return;
          }
        }
      } catch (e) {
        console.error('[staffing] failed to load from Firestore, falling back to localStorage', e);
      }

      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          setDayparts(sortDaypartsByStart(parsed.dayparts || DEFAULT_DAYPARTS));
          setUseSameEveryDay(parsed.useSameEveryDay !== false);
          setTargets(parsed.targets || {});
          console.info('[staffing] loaded targets from localStorage', { locationId });
          return;
        }
      } catch (e) { console.error('[staffing] failed to read localStorage', e); }

      // no saved config: initialize empty targets
      const base = {};
  WEEKDAYS.forEach((d,i) => { base[d] = {}; });
      setTargets(base);
      setDayparts(sortDaypartsByStart(DEFAULT_DAYPARTS));
      setUseSameEveryDay(true);
    })();
  }, [open, storageKey, locationId]);

  // Only show active roles for the currently selected location.
  const roleRows = useMemo(() => {
    const filtered = (roles || []).filter((r) => {
      if (!r || r.archived) return false;
      if (!locationId) return true;
      return r.locationId === locationId;
    });

    // Defensive dedupe by id in case upstream data includes duplicates.
    const byId = new Map();
    filtered.forEach((r) => {
      if (!byId.has(r.id)) byId.set(r.id, { id: r.id, name: r.name || r.id });
    });
    return Array.from(byId.values());
  }, [roles, locationId]);
  const [selectedWeekday, setSelectedWeekday] = useState(WEEKDAYS[0]);

  function saveToStorage() {
    try {
      // Coerce stored target strings to numbers for persistence
      const numericTargets = {};
      Object.keys(targets || {}).forEach(dayKey => {
        numericTargets[dayKey] = {};
        const dpMap = targets[dayKey] || {};
        Object.keys(dpMap).forEach(dpId => {
          numericTargets[dayKey][dpId] = {};
          const roleMap = dpMap[dpId] || {};
          Object.keys(roleMap).forEach(rn => {
            const raw = roleMap[rn];
            const num = Number(raw);
            const safeNum = isNaN(num) ? 0 : Math.min(MAX_DAYPART_THRESHOLD, Math.max(0, num));
            numericTargets[dayKey][dpId][rn] = safeNum;
          });
        });
      });
      const payload = { dayparts, useSameEveryDay, targets: numericTargets };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch (e) {
      // ignore
    }
  }

  function handleSaveAndClose() {
    if (hasOverlap) {
      setSnackbar({
        open: true,
        message: 'Cannot save: daypart times overlap.',
        severity: 'error'
      });
      return;
    }

    (async () => {
      try {
        await waitForAuthReady();
      } catch (e) {}
      // persist to Firestore if possible, otherwise localStorage
      try {
        const tenantId = await tryResolveTenantId();
        if (!locationId) {
          console.warn('[staffing] no location selected - cannot save to Firestore');
          setSnackbar({ open: true, message: 'Select a location before saving targets', severity: 'error' });
          return;
        }
        if (!tenantId) {
          console.warn('[staffing] tenant not resolved - saving to localStorage instead');
          setSnackbar({ open: true, message: 'Tenant not found / not signed in — targets saved locally', severity: 'warning' });
          saveToStorage();
          onClose && onClose();
          return;
        }
        if (tenantId && locationId) {
          const ref = doc(db, `tenants/${tenantId}/staffingTargets`, locationId);
          // save numeric version
          const numericTargets = {};
          Object.keys(targets || {}).forEach(dayKey => {
            numericTargets[dayKey] = {};
            const dpMap = targets[dayKey] || {};
            Object.keys(dpMap).forEach(dpId => {
              numericTargets[dayKey][dpId] = {};
              const roleMap = dpMap[dpId] || {};
              Object.keys(roleMap).forEach(rn => {
                const raw = roleMap[rn];
                const num = Number(raw);
                const safeNum = isNaN(num) ? 0 : Math.min(MAX_DAYPART_THRESHOLD, Math.max(0, num));
                numericTargets[dayKey][dpId][rn] = safeNum;
              });
            });
          });
          await setDoc(ref, { dayparts, useSameEveryDay, targets: numericTargets, updatedAt: new Date() });
          console.info('[staffing] saved targets to Firestore', { tenantId, locationId });
          setSnackbar({ open: true, message: 'Targets saved to Firestore', severity: 'success' });
          onClose && onClose();
          return;
        }
      } catch (e) {
        console.error('[staffing] failed to save to Firestore, falling back to localStorage', e);
        setSnackbar({ open: true, message: 'Failed to save to Firestore — saved locally instead', severity: 'warning' });
      }
      saveToStorage();
      console.info('[staffing] saved targets to localStorage', { locationId });
      onClose && onClose();
    })();
  }

  const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', severity: 'info' });

  function updateDaypart(index, key, value) {
    const copy = dayparts.map((d, i) => i === index ? { ...d, [key]: value } : d);
    setDayparts(sortDaypartsByStart(copy));
  }

  function makeIdFromName(name) {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `dp_${Date.now()}`;
  }

  function addDaypart() {
    const baseName = 'New';
    let id = makeIdFromName(baseName + '_' + Math.floor(Math.random() * 1000));
    // ensure unique
    while (dayparts.some(d => d.id === id)) id = `${id}_${Math.floor(Math.random()*1000)}`;
    const newDp = { id, name: baseName, start: '09:00', end: '17:00' };
    setDayparts(prev => sortDaypartsByStart([...prev, newDp]));
  }

  function removeDaypart(index) {
    if (dayparts.length <= 1) return; // keep at least one daypart
    setDayparts(prev => prev.filter((_, i) => i !== index));
    // Also remove targets entries for this daypart
    const dpId = dayparts[index].id;
    const copy = { ...targets };
    Object.keys(copy).forEach(k => {
      if (copy[k] && copy[k][dpId]) {
        const newDpMap = { ...copy[k] };
        delete newDpMap[dpId];
        copy[k] = newDpMap;
      }
    });
    setTargets(copy);
  }

  // Validate overlaps: dayparts should not overlap when converted to minute ranges on a single 24h loop
  function checkOverlaps() {
    // Build ranges normalized to minutes in [0, 1440) but allow ranges that cross midnight
    const ranges = dayparts.map(dp => {
      const s = toMinutes(dp.start);
      let e = toMinutes(dp.end);
      // treat end <= start as crossing midnight (e += 24h)
      if (e <= s) e += 24 * 60;
      return { id: dp.id, s, e };
    });
    // Compare each pair
    for (let i = 0; i < ranges.length; i++) {
      const a = ranges[i];
      for (let j = i + 1; j < ranges.length; j++) {
        const b = ranges[j];
        // if intervals overlap on the extended timeline
        if (!(a.e <= b.s || b.e <= a.s)) return true;
      }
    }
    return false;
  }

  const hasOverlap = checkOverlaps();

  // Store raw input strings while editing to avoid cursor/value replacement issues.
  function setTargetFor(dayOrAll, daypartId, roleId, value) {
    const copy = { ...targets };
    const key = dayOrAll;
    copy[key] = copy[key] || {};
    copy[key][daypartId] = copy[key][daypartId] || {};
    // keep raw value (string) so user editing isn't normalized mid-typing
    if (value === undefined || value === null || value === '') {
      copy[key][daypartId][roleId] = '';
      setTargets(copy);
      return;
    }
    const parsed = Number(String(value).replace(/[^0-9]/g, ''));
    if (!Number.isFinite(parsed)) {
      copy[key][daypartId][roleId] = '';
    } else {
      copy[key][daypartId][roleId] = String(Math.min(MAX_DAYPART_THRESHOLD, Math.max(0, parsed)));
    }
    setTargets(copy);
  }

  // Return the raw display string (or empty string) for controlled input
  function getTargetFor(dayOrAll, daypartId, roleId) {
  if (!targets) return '';
  const t = targets[dayOrAll] && targets[dayOrAll][daypartId] && targets[dayOrAll][daypartId][roleId];
  if (t === undefined || t === null) return '';
  return String(t);
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 24,
        }
      }}
    >
      <DialogTitle sx={{ pr: 7, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '1.8rem' }}>
          {locationName ? `Staffing Targets - "${locationName}"` : 'Staffing Targets'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Set coverage goals by daypart and role.
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: daypartsOpen ? 1 : 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Dayparts (editable times)</Typography>
              <IconButton size="small" onClick={() => setDaypartsOpen(s => !s)} aria-expanded={daypartsOpen} aria-label={daypartsOpen ? 'Collapse dayparts' : 'Expand dayparts'}>
                {daypartsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={daypartsOpen} timeout="auto" unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dayparts.map((dp, idx) => (
                  <Box
                    key={dp.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1.3fr 0.9fr 0.9fr auto' },
                      gap: 1,
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <TextField size="small" label="Name" value={dp.name} onChange={(e) => updateDaypart(idx, 'name', e.target.value)} />
                    <TextField size="small" label="Start" type="time" value={dp.start} onChange={(e) => updateDaypart(idx, 'start', e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="End" type="time" value={dp.end} onChange={(e) => updateDaypart(idx, 'end', e.target.value)} InputLabelProps={{ shrink: true }} />
                    <IconButton size="small" aria-label="Remove daypart" onClick={() => removeDaypart(idx)} disabled={dayparts.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 1 }}>
                <Button startIcon={<AddIcon />} size="small" onClick={addDaypart} sx={{ textTransform: 'none', fontWeight: 600 }}>
                  Add daypart
                </Button>
              </Box>
            </Collapse>

            {/* show validation even when collapsed */}
            {hasOverlap && <Typography color="error" sx={{ mt: 1 }}>Daypart times overlap. Please adjust so they do not overlap.</Typography>}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Targets</Typography>
              <FormControlLabel control={<Switch checked={useSameEveryDay} onChange={(e) => setUseSameEveryDay(e.target.checked)} />} label="Use same targets every day" />
            </Box>

            {useSameEveryDay ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>Applies to every day</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 360, borderRadius: 1.5 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 160, bgcolor: 'grey.100', fontWeight: 700 }}>Role</TableCell>
                        {dayparts.map(dp => (
                          <TableCell key={dp.id} align="center" sx={{ bgcolor: 'grey.100', fontWeight: 700 }}>{dp.name}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roleRows.map(role => (
                        <TableRow key={role.id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                          {dayparts.map(dp => (
                            <TableCell key={dp.id} align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={getTargetFor('ALL', dp.id, role.id)}
                                onChange={(e) => setTargetFor('ALL', dp.id, role.id, e.target.value)}
                                onFocus={(e) => { if (e.target.value === '0') setTargetFor('ALL', dp.id, role.id, ''); }}
                                inputProps={{ min: 0, max: MAX_DAYPART_THRESHOLD, style: { textAlign: 'center', padding: '6px 8px' } }}
                                sx={{ width: 84 }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>Customize per weekday</Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="weekday-select-label">Weekday</InputLabel>
                    <Select
                      labelId="weekday-select-label"
                      value={selectedWeekday}
                      label="Weekday"
                      onChange={(e) => setSelectedWeekday(e.target.value)}
                    >
                      {WEEKDAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Box>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 360, borderRadius: 1.5 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 160, bgcolor: 'grey.100', fontWeight: 700 }}>Role</TableCell>
                        {dayparts.map(dp => (
                          <TableCell key={dp.id} align="center" sx={{ bgcolor: 'grey.100', fontWeight: 700 }}>{dp.name}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {roleRows.map(role => (
                        <TableRow key={role.id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 600 }}>{role.name}</TableCell>
                          {dayparts.map(dp => (
                            <TableCell key={dp.id} align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={getTargetFor(selectedWeekday, dp.id, role.id)}
                                onChange={(e) => setTargetFor(selectedWeekday, dp.id, role.id, e.target.value)}
                                onFocus={(e) => { if (e.target.value === '0') setTargetFor(selectedWeekday, dp.id, role.id, ''); }}
                                inputProps={{ min: 0, max: MAX_DAYPART_THRESHOLD, style: { textAlign: 'center', padding: '6px 8px' } }}
                                sx={{ width: 84 }}
                              />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button onClick={handleSaveAndClose} variant="contained" disabled={hasOverlap} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Save
        </Button>
      </DialogActions>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
